import {
    keepPreviousData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {
    fetchPracticeUsers,
    invitePracticeUser,
    removePracticeUser,
    type InvitePracticeUserInput
} from '../services/practiceUsers';
import {
    ACCOUNTS_USERS_KEY,
    PRACTICE_USERS_INFINITE_KEY,
    PRACTICE_USERS_KEY,
    PRACTICE_USERS_PAGED_KEY
} from './queryKeys';

export interface PracticeUsersParams {
    page: number;
    limit: number;
    search?: string;
    enabled?: boolean;
}

export function usePracticeUsers(practiceId: string | undefined, params: PracticeUsersParams) {
    const { page, limit, search, enabled = true } = params;
    const query = useQuery({
        queryKey: PRACTICE_USERS_PAGED_KEY(practiceId ?? '', page, limit, search ?? ''),
        queryFn: () => fetchPracticeUsers(practiceId as string, { page, limit, search }),
        enabled: Boolean(practiceId) && enabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData
    });

    return {
        users: query.data?.users ?? [],
        meta: query.data?.meta ?? { total: 0, page, limit, totalPages: 1 },
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch
    };
}

export interface InfinitePracticeUsersParams {
    limit: number;
    search?: string;
    enabled?: boolean;
}

// Mirrors useInfiniteInvoices: the query key omits `page` on purpose, so a
// search change starts a fresh page-1 fetch rather than appending after stale rows.
export function useInfinitePracticeUsers(
    practiceId: string | undefined,
    params: InfinitePracticeUsersParams
) {
    const { limit, search, enabled = true } = params;
    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        isError,
        error
    } = useInfiniteQuery({
        queryKey: PRACTICE_USERS_INFINITE_KEY(practiceId ?? '', limit, search ?? ''),
        initialPageParam: 1,
        queryFn: ({ pageParam }) =>
            fetchPracticeUsers(practiceId as string, { page: pageParam as number, limit, search }),
        getNextPageParam: (lastPage) =>
            lastPage.meta.page * lastPage.meta.limit < lastPage.meta.total
                ? lastPage.meta.page + 1
                : undefined,
        enabled: Boolean(practiceId) && enabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData
    });

    const firstPageMeta = data?.pages[0]?.meta;

    return {
        users: data?.pages.flatMap((page) => page.users) ?? [],
        meta: firstPageMeta ?? { total: 0, page: 1, limit, totalPages: 1 },
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage,
        isError,
        error
    };
}

// Linking a user to a practice changes both the practice's Users tab and the
// global Accounts > Users directory, so both keys are invalidated.
export function useInvitePracticeUser(practiceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: InvitePracticeUserInput) => invitePracticeUser(practiceId, input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PRACTICE_USERS_KEY(practiceId) });
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_USERS_KEY] });
        }
    });
}

export function useRemovePracticeUser(practiceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (email: string) => removePracticeUser(practiceId, email),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PRACTICE_USERS_KEY(practiceId) });
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_USERS_KEY] });
        }
    });
}
